import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOGOUT_MODAL, TOKEN_ERRORS } from '@/utilities/constants';
import { decodeBase64, getLogoutModalContent } from '@/utilities/utilities';
import { useStore } from '@/redux/store';
import { AuthAPI } from '@/services/api/auth';
import { ActionModal } from 'br2s-ui-componentlibrary_r2_02';
import 'br2s-ui-componentlibrary_r2_02/dist/foundation/foundation.scss';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import {
  AuthService,
  LocalStorageService,
  reportDtCustomError,
  useHTTPError,
} from 'br2s-ui-common-utils-02';
import { isAxiosError } from 'axios';
import { flushSync } from 'react-dom';

export interface ButtonProps {
  id: string;
  type?: 'primary' | 'secondary' | 'tertiary' | 'quiet';
  size?: 'medium' | 'large';
  isBlock?: boolean;
  iconClassName?: SystemIcon;
  iconType?: 'no-icon' | 'prepended' | 'appended';
  isIconButton?: boolean;
  isDisabled?: boolean;
  label?: string;
  ariaLabel?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  isDropdownButton?: boolean;
  isDropdownButtonExpanded?: boolean;
}

interface ILogoutModal {
  isOpen: boolean;
  state?: 'informative' | 'success' | 'warning' | 'error';
  title?: string;
  message?: string;
  actionTitle?: string;
  actionButton1?: ButtonProps;
  actionButton2: ButtonProps;
  onCloseClick?: MouseEventHandler<HTMLElement>;
}

interface LogoutProps {
  showLogout: boolean;
  setShowLogout: (show: boolean) => void;
  onLogout: () => void;
  setModalType: (type: string) => void;
  modalType?: string;
  setCookie: (
    name: 'sessionType' | 'isSessionActive',
    value: string,
    options?: { maxAge?: number; path: string } | undefined,
  ) => void;
}

const envInactivityTimeout = process.env.REACT_APP_INACTIVITY_TIMER;
const envLogoutTimeout = process.env.REACT_APP_LOGOUT_TIMER;
const inactivityTimeout = envInactivityTimeout
  ? Number(envInactivityTimeout)
  : LOGOUT_MODAL.INACTIVITY_TIMER_AUTH_USER;
const logoutTimeout = envLogoutTimeout
  ? Number(envLogoutTimeout)
  : LOGOUT_MODAL.LOGOUT_TIMER;

const getLastActiveTime = () => {
  const lastActiveTime = localStorage.getItem('lastActiveTime');
  if (!lastActiveTime) return Date.now();

  const decoded = decodeBase64(lastActiveTime);
  return decoded ? Number(decoded) : Date.now();
};

const LogoutModal = ({
  showLogout,
  setShowLogout,
  onLogout,
  setModalType,
  setCookie,
}: LogoutProps) => {
  const { logoff } = useStore();
  const { handleHTTPError } = useHTTPError();
  const navigate = useNavigate();

  const [isInactive, setIsInactive] = useState(false);
  const [logoutTimer, setLogoutTimer] = useState(logoutTimeout);
  const [logoutRetries, setLogoutRetries] = useState(0);
  const [isRefreshingOrLoggingOut, setIsRefreshingOrLoggingOut] =
    useState(false);
  const lastActiveTime = useRef(getLastActiveTime());

  const setLastActiveTime = (value: number) => {
    lastActiveTime.current = value;
  };

  useEffect(() => {
    handleIdle();

    window.addEventListener('storage', handleStorageActivity);

    return () => {
      window.removeEventListener('storage', handleStorageActivity);
    };
  }, []);

  useEffect(() => {
    // istanbul ignore next
    if (logoutRetries > LOGOUT_MODAL.LOGOUT_MAX_RETRIES) {
      window.dispatchEvent(new Event('refresh_token_counter_expire'));
      return;
    }

    const handleUserActivity = () => {
      // Don't log user activity when session timeout modal is shown
      if (isInactive || !LocalStorageService.hasAuth()) return;

      const lastActiveTime = Date.now();
      setLastActiveTime(lastActiveTime);
      localStorage.setItem('lastActiveTime', btoa(String(lastActiveTime)));
    };

    const eventTypes = LOGOUT_MODAL.EVENTS;
    eventTypes.forEach((event) =>
      window.addEventListener(event, handleUserActivity),
    );

    const idleTimer = setInterval(() => {
      handleIdle();
    }, 1000);

    return () => {
      eventTypes.forEach((event) =>
        window.removeEventListener(event, handleUserActivity),
      );

      clearInterval(idleTimer);
    };
  }, [isInactive, showLogout, logoutRetries, isRefreshingOrLoggingOut]);

  // istanbul ignore next
  const handleIdle = () => {
    // Prompt will come 18 mins after the time user was last active
    const timeoutPromptTime = lastActiveTime.current + inactivityTimeout;

    // Log out 2 mins after session timeout modal prompts
    if (
      isInactive &&
      Math.abs(Date.now() - timeoutPromptTime) > logoutTimeout
    ) {
      handleLogout('session');
      return;
    }

    // If the difference of current time and last active time is greater
    // than 18 minutes, show the session timeout modal
    if (Math.abs(Date.now() - lastActiveTime.current) > inactivityTimeout) {
      setIsInactive(true);
      setShowLogout(true);

      // Start timer countdown only when session timeout modal shows
      const timer = logoutTimeout - (Date.now() - timeoutPromptTime);
      setLogoutTimer(timer > 0 ? timer : 0);
    }
  };

  // istanbul ignore next
  const handleStorageActivity = (event: StorageEvent) => {
    // sync activity from another tab
    if (event.key === 'lastActiveTime' && event.newValue) {
      const decoded = decodeBase64(event.newValue);
      if (decoded) {
        setLastActiveTime(Number(decoded));
        setShowLogout(false);
        setIsInactive(false);
      }
    }

    // logout all other tabs when 1 tab logs out
    if (
      event.key === 'auth' &&
      typeof event.oldValue === 'string' &&
      event.newValue === null
    ) {
      setShowLogout(true);
      setIsInactive(false);
    }
  };

  const handleClose = async () => {
    if (!LocalStorageService.hasAuth()) {
      handleClick();
      return;
    }

    // istanbul ignore next
    if (isInactive) {
      handleRefreshToken();
      return;
    }

    setShowLogout(false);
  };

  const handleRefreshToken = () => {
    if (isRefreshingOrLoggingOut) return;

    setIsRefreshingOrLoggingOut(true);
    AuthService.refreshToken(process.env.REACT_APP_BASE_URL as string)
      .then((refreshToken) => {
        if (refreshToken === true) {
          setLogoutRetries(0);
          setIsInactive(false);
          setShowLogout(false);
          const lastActiveTime = Date.now();
          localStorage.setItem('lastActiveTime', btoa(String(lastActiveTime)));
          setLastActiveTime(lastActiveTime);
        }
      })
      .catch((error) => {
        console.error(error);
        reportDtCustomError('GenericHandledError', JSON.stringify(error));
      })
      .finally(() => {
        setIsRefreshingOrLoggingOut(false);
      });
  };

  const handleLogout = async (type = 'logout') => {
    if (isRefreshingOrLoggingOut) return;

    setIsRefreshingOrLoggingOut(true);

    try {
      const response = await AuthAPI.sessionLogout();
      if (response?.status === 200) {
        setModalType(type);
        localStorage.removeItem('auth');
        localStorage.removeItem('lastActiveTime');
        setCookie('isSessionActive', '', { maxAge: 0, path: '/' });
        flushSync(() => logoff());
        onLogout();
        setShowLogout(false);
      }
    } catch (error) {
      setLogoutRetries((count) => count + 1); // increment logout retry everytime logout API fails
      console.error(error);
      reportDtCustomError('GenericHandledError', JSON.stringify(error));

      // istanbul ignore next
      if (
        isAxiosError(error) &&
        Object.values(TOKEN_ERRORS).includes(
          error.response?.data?.error?.errorCode,
        )
      ) {
        setShowLogout(false);
      }

      // istanbul ignore next
      handleHTTPError(error, {
        errorType: 'inline',
      });
    } finally {
      setIsRefreshingOrLoggingOut(false);
    }
  };

  const handleClick = () => {
    if (secondBtn === LOGOUT_MODAL.BACK_TO_HOMEPAGE_LABEL) {
      setShowLogout(false);
      flushSync(() => logoff());
      onLogout();
      navigate('/ ');
    } else {
      handleLogout();
    }
  };

  const minutes = Math.floor(logoutTimer / 60000);
  const seconds = Math.floor((logoutTimer % 60000) / 1000);
  const formatted = `${minutes}m ${seconds}s`;
  const { state, title, description, message, firstBtn, secondBtn } =
    getLogoutModalContent(isInactive, formatted);

  const logout: ILogoutModal = {
    isOpen: showLogout,
    state,
    title,
    actionTitle: description,
    message,
    ...(firstBtn
      ? {
          actionButton1: {
            id: 'cancel',
            type: 'secondary',
            label: firstBtn,
            onClick: () => {
              isInactive ? handleLogout() : handleClose();
            },
          },
        }
      : {}),
    actionButton2: {
      id: 'logout',
      type: 'primary',
      label: secondBtn,
      onClick: () => {
        isInactive ? handleClose() : handleClick();
      },
    },
  };

  return (
    <div>
      <ActionModal
        id="logout-modal"
        isOpen={logout.isOpen}
        title={logout.title}
        state={logout.state}
        message={logout.message}
        actionTitle={logout.actionTitle}
        actionButton1={logout.actionButton1}
        actionButton2={logout.actionButton2}
        onCloseClick={handleClose}
      />
    </div>
  );
};

export default LogoutModal;
